import React from 'react';
import Icon from '../../../components/AppIcon';

const SyncStatistics = ({ stats, className = '' }) => {
  const getSuccessRate = () => {
    const total = stats.totalTracks;
    const successful = stats.exactMatches + stats.similarMatches;
    return total > 0 ? Math.round((successful / total) * 100) : 0;
  };

  const getEstimatedTime = () => {
    const tracksToSync = stats.exactMatches + stats.similarMatches;
    const estimatedMinutes = Math.ceil(tracksToSync / 10); // Assume 10 tracks per minute
    return estimatedMinutes;
  };

  const statisticsData = [
    {
      label: 'Total Tracks',
      value: stats.totalTracks,
      icon: 'List',
      color: 'text-text-primary',
      bgColor: 'bg-surface-secondary'
    },
    {
      label: 'Exact Matches',
      value: stats.exactMatches,
      icon: 'CheckCircle',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      label: 'Similar Matches',
      value: stats.similarMatches,
      icon: 'AlertCircle',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    },
    {
      label: 'No Matches',
      value: stats.noMatches,
      icon: 'XCircle',
      color: 'text-error',
      bgColor: 'bg-error/10'
    },
    {
      label: 'Success Rate',
      value: `${getSuccessRate()}%`,
      icon: 'TrendingUp',
      color: getSuccessRate() >= 80 ? 'text-success' : getSuccessRate() >= 60 ? 'text-warning' : 'text-error',
      bgColor: getSuccessRate() >= 80 ? 'bg-success/10' : getSuccessRate() >= 60 ? 'bg-warning/10' : 'bg-error/10'
    },
    {
      label: 'Est. Time',
      value: `${getEstimatedTime()}m`,
      icon: 'Clock',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    }
  ];

  return (
    <div className={`bg-surface border border-border rounded-lg p-4 lg:p-6 ${className}`}>
      <div className="flex items-center space-x-2 mb-4">
        <Icon name="BarChart3" size={20} className="text-primary" />
        <h3 className="text-lg font-semibold text-text-primary">Sync Statistics</h3>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {statisticsData.map((stat, index) => (
          <div key={index} className={`p-4 rounded-lg ${stat.bgColor}`}>
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <Icon name={stat.icon} size={20} className={stat.color} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-text-muted uppercase tracking-wide font-medium">
                  {stat.label}
                </p>
                <p className={`text-xl font-bold ${stat.color} mt-1`}>
                  {stat.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Progress Bar */}
      <div className="mt-6">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-text-muted">Match Quality Distribution</span>
          <span className="text-text-secondary">{getSuccessRate()}% Success Rate</span>
        </div>
        <div className="w-full bg-surface-secondary rounded-full h-3 overflow-hidden">
          <div className="h-full flex">
            <div 
              className="bg-success transition-all duration-500"
              style={{ width: `${(stats.exactMatches / stats.totalTracks) * 100}%` }}
            ></div>
            <div 
              className="bg-warning transition-all duration-500"
              style={{ width: `${(stats.similarMatches / stats.totalTracks) * 100}%` }}
            ></div>
            <div 
              className="bg-error transition-all duration-500"
              style={{ width: `${(stats.noMatches / stats.totalTracks) * 100}%` }}
            ></div>
          </div>
        </div>
        <div className="flex justify-between text-xs text-text-muted mt-2">
          <span>Exact</span>
          <span>Similar</span>
          <span>No Match</span>
        </div>
      </div>

      {/* Warnings */}
      {stats.noMatches > 0 && (
        <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-warning">
                {stats.noMatches} track{stats.noMatches !== 1 ? 's' : ''} could not be matched
              </p>
              <p className="text-xs text-text-muted mt-1">
                These tracks will be skipped during the sync process. You can manually review and select alternatives.
              </p>
            </div>
          </div>
        </div>
      )}

      {getSuccessRate() === 100 && (
        <div className="mt-4 p-3 bg-success/10 border border-success/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <p className="text-sm font-medium text-success">
              Perfect match! All tracks are ready for sync.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncStatistics;